/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, JSX } from 'preact';
import { Grid } from '../UNSAFE_Grid';
import { classNames } from '../utils/UNSAFE_classNames';
import { styles } from './themes/ListItemLayoutStyles.css';

type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Props = {
  children?: ComponentChildren;
  /**
   * The centermost element that should display more important data.
   */
  primary?: ComponentChildren;
  /**
   * Render Selector component in the leftmost column of the grid.
   */
  selector?: ComponentChildren;
  /**
   * Visual element to the left of the primary section.
   * Supports image, avatar, badge, initials, or icon.
   */
  leading?: ComponentChildren;
  /**
   * A section that is rendered above the primary line.
   */
  overline?: ComponentChildren;
  /**
   * A section below the primary line.
   */
  secondary?: ComponentChildren;
  /**
   * Another section below secondary.
   */
  tertiary?: ComponentChildren;
  /**
   * Provides information about other data.
   * Some examples of basic metadata are author, date created, date modified, file size, abbreviations, etc.
   */
  metadata?: ComponentChildren;
  /**
   * Visual element to the right of the primary section and metadata.
   * Example components are image, avatar, badge, initials, or icon.
   */
  trailing?: ComponentChildren;
  /**
   * The action prop displays either one primary action or one or more secondary actions.
   * A common example is using Toolbar.
   */
  action?: ComponentChildren;
  /**
   * Additional section separated with a space between itself and tertiary.
   */
  quaternary?: ComponentChildren;
  /**
   * Navigation should normally be used for linking away from the layout.
   */
  navigation?: ComponentChildren;
} & IntrinsicProps;

// TODO: revisit idea of negative margin when selector has a proper API for handling spacing
// Jira: JET-54543

export function ListItemLayout({
  children,
  selector,
  leading,
  primary,
  secondary,
  tertiary,
  overline,
  metadata,
  trailing,
  action,
  quaternary,
  navigation
}: Props) {
  // generate grid skeleton, adding cells based on which props are defined
  const { columnsTemplate, firstCell, secondCell, lastCell, hasSecondRow } = getColumnsTemplate(
    selector,
    leading,
    metadata,
    trailing,
    action,
    quaternary,
    navigation
  );

  // if ListItemLayout component has a child (ListItemTextLayout) then render that instead of individual props
  const textArea = children ? (
    <div className={styles.childrenStyles}>{children}</div>
  ) : (
    <div className={styles.flexColumnStyles}>
      <div>{overline}</div>
      <div>{primary}</div>
      <div>{secondary}</div>
      <div>{tertiary}</div>
    </div>
  );

  // a second row is needed when quaternary or navigation are defined
  const secondRow = hasSecondRow
    ? [
        firstCell && <span> </span>,
        secondCell && <span> </span>,
        <div className={styles.rowAlignStyles}>{quaternary}</div>,
        lastCell && <div className={styles.endJustifyStyles}>{navigation}</div>
      ]
    : null;

  return (
    <Grid gap="4x" gridTemplateColumns={columnsTemplate}>
      {firstCell}
      {secondCell}
      <div className={styles.centerVerticalStyles}>{textArea}</div>
      {lastCell}
      {secondRow}
    </Grid>
  );
}

function getColumnsTemplate(
  selector: ComponentChildren,
  leading: ComponentChildren,
  metadata: ComponentChildren,
  trailing: ComponentChildren,
  action: ComponentChildren,
  quaternary: ComponentChildren,
  navigation: ComponentChildren
) {
  const columnsTemplateBuilder = [];

  let firstCell = null;
  if (selector) {
    columnsTemplateBuilder.push('auto');
    firstCell = <div className={styles.selectorStyles}>{selector}</div>;
  }

  let secondCell = null;
  if (leading) {
    columnsTemplateBuilder.push('auto');
    secondCell = <div className={styles.centerVerticalStyles}>{leading}</div>;
  }

  columnsTemplateBuilder.push('1fr'); // Text area always gets 1 full fraction of allotted space

  let lastCell = null;
  if (metadata || trailing || action || navigation) {
    columnsTemplateBuilder.push('auto');
    lastCell = (
      <div className={styles.flexRowStyles}>
        <div
          className={classNames([
            styles.centerVerticalStyles,
            metadata ? styles.leftPaddingStyles : ''
          ])}>
          {metadata}
        </div>
        <div
          className={classNames([
            styles.centerVerticalStyles,
            trailing ? styles.leftPaddingStyles : ''
          ])}>
          {trailing}
        </div>
        <div
          className={classNames([
            styles.centerVerticalStyles,
            action ? styles.leftPaddingStyles : ''
          ])}>
          {action}
        </div>
      </div>
    );
  }

  const columnsTemplate = columnsTemplateBuilder.join(' ');
  const hasSecondRow = quaternary || navigation ? true : false;

  return { columnsTemplate, firstCell, secondCell, lastCell, hasSecondRow };
}
